import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ChatSidebar from "@/components/ChatSidebar";
import ChatMessage from "@/components/ChatMessage";
import ChatInput from "@/components/ChatInput";
import StoneLogo from "@/components/StoneLogo";
import {
  createConversation,
  getConversation,
  listConversations,
  patchConversation,
  deleteConversation,
  streamChat,
  uploadFile,
} from "@/lib/api";
import { motion } from "framer-motion";
import { Sparkles, Eye, FileText, Layers, Menu } from "lucide-react";

const SUGGESTIONS = [
  { icon: Sparkles, t: "Compare Calacatta Borghini com Quartzito Patagonia para bancada." },
  { icon: Eye, t: "Analise esta foto e identifique a pedra (envie uma imagem)." },
  { icon: FileText, t: "Leia este PDF de catálogo e resuma a ficha técnica." },
  { icon: Layers, t: "Quais patologias o Travertino Romano apresenta em fachada?" },
];

const ChatPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(conversationId || null);
  const [messages, setMessages] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const scrollRef = useRef(null);

  const refreshConversations = async () => {
    try {
      const list = await listConversations();
      setConversations(list);
      return list;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  useEffect(() => { refreshConversations(); }, []);

  useEffect(() => {
    if (!conversationId) {
      setActiveId(null);
      setMessages([]);
      return;
    }
    setActiveId(conversationId);
    (async () => {
      try {
        const data = await getConversation(conversationId);
        setMessages(data.messages || []);
      } catch (e) {
        toast.error("Conversa não encontrada");
        navigate("/chat");
      }
    })();
  }, [conversationId, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText]);

  const handleNewChat = async () => {
    try {
      const c = await createConversation();
      await refreshConversations();
      navigate(`/chat/${c.id}`);
    } catch (e) {
      toast.error("Erro ao criar nova conversa");
    }
  };

  const handleSelectConversation = (id) => {
    if (isStreaming) return;
    navigate(`/chat/${id}`);
  };

  const handleDelete = async (id) => {
    try {
      await deleteConversation(id);
      toast.success("Conversa removida");
      await refreshConversations();
      if (id === activeId) navigate("/chat");
    } catch (e) {
      toast.error("Erro ao remover");
    }
  };

  const handleToggleFavorite = async (id, current) => {
    try {
      await patchConversation(id, { favorite: !current });
      await refreshConversations();
    } catch (e) {
      toast.error("Erro");
    }
  };

  const handleSend = async (text, file) => {
    if (!text.trim() && !file) return;
    let convId = activeId;
    if (!convId) {
      try {
        const c = await createConversation();
        convId = c.id;
        setActiveId(c.id);
        navigate(`/chat/${c.id}`, { replace: true });
        await refreshConversations();
      } catch (e) {
        toast.error("Erro ao criar conversa");
        return;
      }
    }

    let fileMeta = null;
    if (file) {
      try {
        fileMeta = await uploadFile(file);
      } catch (e) {
        toast.error("Falha no upload do arquivo");
        return;
      }
    }

    const tempUser = {
      id: `temp-user-${Date.now()}`,
      role: "user",
      content: text,
      file_id: fileMeta?.file_id || null,
      file_name: fileMeta?.filename || null,
      file_type: fileMeta?.type || null,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);
    setStreamingText("");
    setIsStreaming(true);

    let acc = "";
    try {
      await streamChat({
        conversationId: convId,
        message: text,
        fileId: fileMeta?.file_id,
        onToken: (t) => {
          acc += t;
          setStreamingText(acc);
        },
        onDone: async () => {
          setMessages((m) => [
            ...m,
            {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: acc,
              created_at: new Date().toISOString(),
            },
          ]);
          setStreamingText("");
          setIsStreaming(false);
          await refreshConversations();
        },
        onError: (e) => {
          toast.error(e.message || "Erro ao gerar resposta");
          setIsStreaming(false);
          setStreamingText("");
        },
      });
    } catch (e) {
      toast.error("Erro de conexão");
      setIsStreaming(false);
      setStreamingText("");
    }
  };

  return (
    <div className="h-screen flex bg-[#3A3A3A] text-zinc-100 overflow-hidden" data-testid="chat-page">
      <ChatSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversations}
        activeId={activeId}
        onNewChat={handleNewChat}
        onSelect={handleSelectConversation}
        onDelete={handleDelete}
        onToggleFavorite={handleToggleFavorite}
      />
      <main className="flex-1 flex flex-col relative overflow-hidden">
        <header className="h-20 border-b border-[#555555] bg-[#3A3A3A]/95 backdrop-blur-xl flex items-center px-5 gap-3 shrink-0" data-testid="chat-header">
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-zinc-400 hover:text-white p-1.5 rounded-md hover:bg-white/5"
              data-testid="open-sidebar-btn"
            >
              <Menu size={18} />
            </button>
          )}
          <StoneLogo size={44} wordmarkSize={24} />
          <div className="ml-auto text-[12px] tracking-[0.22em] uppercase font-medium" style={{ color: "#C9A84C" }}>
            Inteligência Mineral
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-hide pb-44" data-testid="chat-scroll-area">
          <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-10">
            {messages.length === 0 && !streamingText ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="pt-20"
                data-testid="chat-empty-state"
              >
                <h1 className="silver-text text-4xl md:text-5xl font-light tracking-tighter" style={{ fontFamily: "Outfit" }}>
                  Como posso ajudá-lo no universo mineral?
                </h1>
                <p className="text-zinc-500 mt-4 max-w-xl leading-relaxed">
                  Pergunte sobre pedras, aplicações, manutenção, patologias, instalação ou engenharia mineral.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-12">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(s.t)}
                      className="text-left p-5 rounded-2xl border border-[#555555] bg-[#454545] hover:border-[#C9A84C]/60 hover:bg-[#4D4D4D] transition-all duration-300 group"
                      data-testid={`suggestion-${i}`}
                    >
                      <s.icon className="h-4 w-4 text-zinc-200 mb-3 group-hover:text-[#C9A84C] transition" strokeWidth={1.5} />
                      <div className="text-[13.5px] text-zinc-100 leading-relaxed">{s.t}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <div className="space-y-7">
                {messages.map((m) => (
                  <ChatMessage key={m.id} message={m} />
                ))}
                {isStreaming && (
                  <ChatMessage
                    message={{
                      id: "streaming",
                      role: "assistant",
                      content: streamingText,
                    }}
                    streaming
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <ChatInput onSend={handleSend} disabled={isStreaming} />
      </main>
    </div>
  );
};

export default ChatPage;
